// The 11 account-level OAuth identities — v3 `utils/integrations.js:76-214` (`AUTHENTICATIONS`).
//
// Ported verbatim apart from the renames in `providers.types.ts` and one deliberate drop:
// v3's `integrationNames` was a display-fragment array the auth card concatenated into a label
// ('Meta' + ' Ads' + 'meta_catalog' + ' Catalog' + …). It is neither i18n-able nor derivable, and
// the same label is reachable from `channelKeys` → `CHANNEL_DISPLAY[key].nameKey`, which is
// translated. `AuthProviderCard` (ORP-18698) builds its label from `channelKeys`.

import type { AuthFamily } from './providers.types'

export const AUTH_FAMILIES: readonly AuthFamily[] = [
  {
    field: 'meta',
    nameKey: 'integrations.authFamilies.meta',
    connectedKey: 'meta_email',
    connectedFlag: 'meta_connected',
    refreshKey: 'need_facebook_refresh',
    scopeKey: 'need_facebook_scope_refresh',
    authUrl: 'oauth/facebook/login',
    logoSize: 16,
    // Verbatim v3 (`utils/integrations.js:85`), and deliberately NOT widened to the Meta
    // sub-entities. v3's dropped `integrationNames` did name a catalog — and "Meta Pages", which
    // has no channel at all — but that array was display text, not this gating list. Parentage
    // lives in exactly one place: `subEntityOf` on the display rows.
    channelKeys: ['meta_ads_account'],
    // The Authentication row's logos, which v3 listed separately (`integrations: [...]`).
    badgeKeys: ['meta_ads_account', 'facebook_page'],
    logo: 'meta_ads',
  },
  {
    field: 'tiktok',
    nameKey: 'integrations.authFamilies.tiktok',
    connectedKey: 'tiktok_oauth',
    defaultText: true,
    authUrl: 'oauth/tiktok/login',
    channelKeys: ['tiktok_ads_account', 'tiktok_pixel', 'tiktok_catalog'],
    logo: 'tiktok',
  },
  {
    field: 'google',
    nameKey: 'integrations.authFamilies.google',
    connectedKey: 'google_email',
    connectedFlag: 'google_connected',
    refreshKey: 'need_google_refresh',
    scopeKey: 'need_google_scope_refresh',
    authUrl: 'oauth/google/login',
    // v3's list omits `bigquery_account` even though its four variants authenticate with
    // `google_email`; kept as-is — the auth card's channel list is display, not gating.
    channelKeys: [
      'ga4_analytics_property',
      'google_ads_account',
      'search_console',
      'merchant_center_account',
      'youtube_channel',
      'playstore_account',
      'dv360_advertiser',
      'cm360_advertiser',
      'gtm_container',
    ],
    logo: 'google',
  },
  {
    field: 'criteo',
    nameKey: 'integrations.authFamilies.criteo',
    connectedKey: 'criteo_oauth',
    defaultText: true,
    authUrl: 'oauth/criteo/login',
    channelKeys: ['criteo_account'],
    logo: 'criteo',
  },
  {
    field: 'linkedin',
    nameKey: 'integrations.authFamilies.linkedin',
    connectedKey: 'linkedin_oauth',
    defaultText: true,
    authUrl: 'oauth/linkedin/login',
    channelKeys: ['linkedin_ads_account'],
    logo: 'linkedin_ads',
  },
  {
    field: 'snapchat',
    nameKey: 'integrations.authFamilies.snapchat',
    connectedKey: 'snapchat_oauth',
    defaultText: true,
    authUrl: 'oauth/snapchat/login',
    channelKeys: ['snapchat_account'],
    logo: 'snapchat_ads',
  },
  {
    field: 'yandex',
    nameKey: 'integrations.authFamilies.yandex',
    // Hyphenated where every sibling uses an underscore, and `/redirect` where every sibling uses
    // `/login`. Both are the live v3 values (`utils/integrations.js:161,163`) — not typos to fix.
    connectedKey: 'yandex-oauth',
    defaultText: true,
    authUrl: 'oauth/yandex/redirect',
    channelKeys: ['yandex_direct_account'],
    logo: 'yandex',
  },
  {
    field: 'twitter',
    nameKey: 'integrations.authFamilies.twitter',
    connectedKey: 'twitter_auth',
    defaultText: true,
    authUrl: 'oauth/twitter/login',
    logoSize: 20,
    channelKeys: ['twitter_ad_account'],
    logo: 'twitter',
  },
  {
    field: 'bing',
    nameKey: 'integrations.authFamilies.bing',
    connectedKey: 'bing_oauth',
    refreshKey: 'need_bing_refresh',
    scopeKey: 'need_bing_scope_refresh',
    defaultText: true,
    authUrl: 'oauth/bing/login',
    logoSize: 20,
    channelKeys: ['bing_ads_account'],
    logo: 'bing_ads',
  },
  {
    field: 'pinterest',
    nameKey: 'integrations.authFamilies.pinterest',
    // Misspelled on the wire ('pinterets'). Preserved — it is the `accountDetail` flag name.
    connectedKey: 'pinterets_oauth',
    defaultText: true,
    authUrl: 'oauth/pinterest/login',
    logoSize: 20,
    channelKeys: ['pinterest_account'],
    logo: 'pinterest',
  },
  {
    field: 'reddit',
    nameKey: 'integrations.authFamilies.reddit',
    connectedKey: 'reddit_oauth',
    defaultText: true,
    authUrl: 'oauth/reddit/login',
    logoSize: 20,
    channelKeys: ['reddit_ad_account'],
    logo: 'reddit',
  },
]

const BY_CONNECTED_KEY = new Map(AUTH_FAMILIES.map((family) => [family.connectedKey, family]))

/** The family a provider's `authFamily` points at, or `null` for credential providers. */
export function resolveAuthFamily(connectedKey: string | undefined): AuthFamily | null {
  return connectedKey ? (BY_CONNECTED_KEY.get(connectedKey) ?? null) : null
}
